// pages/RideHistoryDetail.jsx
import React, { useEffect, useState } from "react";
import {
  doc,
  getDoc,
  getDocs,
  collection,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";
import { COLLECTION_NAME } from "../../constants/endpoints";
import { formatDate, formatFirebaseTime } from "../../utils";
import Loader from "../../Components/Loader/Loader";
import { Descriptions, Collapse, Tag } from "antd";
import { Bounce, toast } from "react-toastify";
import Table from "../../Components/Table/Table";
import { FiArrowLeft } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";

// 🧹 Format helper
const formatReason = (reasonKey) => {
  if (!reasonKey) return "N/A";
  return reasonKey
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const formatActionLabel = (action) => {
  if (!action) return "Unknown";
  return action
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const toRadians = (value) => (value * Math.PI) / 180;

const getDistanceInKm = (pointA, pointB) => {
  if (
    !pointA ||
    !pointB ||
    pointA.latitude == null ||
    pointA.longitude == null ||
    pointB.latitude == null ||
    pointB.longitude == null
  ) {
    return Number.POSITIVE_INFINITY;
  }

  const earthRadiusKm = 6371;
  const dLat = toRadians(pointB.latitude - pointA.latitude);
  const dLon = toRadians(pointB.longitude - pointA.longitude);
  const lat1 = toRadians(pointA.latitude);
  const lat2 = toRadians(pointB.latitude);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.sin(dLon / 2) *
    Math.sin(dLon / 2) *
    Math.cos(lat1) *
    Math.cos(lat2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadiusKm * c;
};

const getMapEmbedUrl = (latitude, longitude) => {
  if (latitude == null || longitude == null) return "";

  const delta = 0.008;
  const left = longitude - delta;
  const right = longitude + delta;
  const top = latitude + delta;
  const bottom = latitude - delta;

  return `https://www.openstreetmap.org/export/embed.html?bbox=${left}%2C${bottom}%2C${right}%2C${top}&layer=mapnik&marker=${latitude}%2C${longitude}`;
};

const parseCoordinateArray = (value) => {
  if (!Array.isArray(value) || value.length < 2) return null;

  const first = Number(value[0]);
  const second = Number(value[1]);
  if (Number.isNaN(first) || Number.isNaN(second)) return null;

  const firstCanBeLat = Math.abs(first) <= 90;
  const secondCanBeLat = Math.abs(second) <= 90;
  const firstCanBeLng = Math.abs(first) <= 180;
  const secondCanBeLng = Math.abs(second) <= 180;

  // Prefer [lat, lng] when it clearly matches that pattern.
  if (firstCanBeLat && secondCanBeLng) {
    return { latitude: first, longitude: second };
  }

  // Fallback to GeoJSON-style [lng, lat].
  if (firstCanBeLng && secondCanBeLat) {
    return { latitude: second, longitude: first };
  }

  return null;
};

const getCoordinatesFromValue = (value) => {
  if (!value) return null;

  if (typeof value === "string") {
    const parts = value.split(",").map((part) => Number(part.trim()));
    return parseCoordinateArray(parts);
  }

  if (Array.isArray(value)) return parseCoordinateArray(value);
  if (typeof value !== "object") return null;

  const latitudeCandidates = [
    value.latitude,
    value.lat,
    value._lat,
    value._latitude,
  ];
  const longitudeCandidates = [
    value.longitude,
    value.lng,
    value.lon,
    value.long,
    value._long,
    value._lng,
    value._longitude,
  ];

  const latitude = latitudeCandidates.find(
    (candidate) => candidate != null && !Number.isNaN(Number(candidate))
  );
  const longitude = longitudeCandidates.find(
    (candidate) => candidate != null && !Number.isNaN(Number(candidate))
  );

  if (latitude == null || longitude == null) return null;

  return {
    latitude: Number(latitude),
    longitude: Number(longitude),
  };
};

const getEventCoordinates = (event) => {
  if (!event || typeof event !== "object") return null;

  // Support different payload shapes:
  // 1) event.coordinates.latitude/longitude
  // 2) event.coordinates.coordinates.latitude/longitude
  // 3) event.location.latitude/longitude
  // 4) event.location.coordinates.latitude/longitude
  const directCoordinates = getCoordinatesFromValue(event.coordinates);
  if (directCoordinates) return directCoordinates;

  const nestedCoordinates = getCoordinatesFromValue(event?.coordinates?.coordinates);
  if (nestedCoordinates) return nestedCoordinates;

  const locationCoordinates = getCoordinatesFromValue(event.location);
  if (locationCoordinates) return locationCoordinates;

  const nestedLocationCoordinates = getCoordinatesFromValue(event?.location?.coordinates);
  if (nestedLocationCoordinates) return nestedLocationCoordinates;

  const stageCoordinates = [
    event?.pickupLocation,
    event?.dropLocation,
    event?.destinationLocation,
    event?.currentLocation,
    event?.stageLocation,
  ]
    .map((candidate) => getCoordinatesFromValue(candidate))
    .find(Boolean);
  if (stageCoordinates) return stageCoordinates;

  return getCoordinatesFromValue(event?.geo);
};

const getStageLocationLabel = (event, ride) => {
  const action = event?.action?.toLowerCase() || "";
  const eventCoordinates = getEventCoordinates(event);
  const pickupCoordinates = getCoordinatesFromValue(ride?.pickupLocation);
  const destinationCoordinates = getCoordinatesFromValue(ride?.dropLocation);

  const pickupLabel = ride?.pickupAddress
    ? `Pickup Location: ${ride.pickupAddress}`
    : "Pickup Location";
  const destinationLabel = ride?.destinationAddress
    ? `Destination Location: ${ride.destinationAddress}`
    : "Destination Location";

  const pickupStageActions = [
    "created",
    "pending",
    "scheduled",
    "accepted",
    "arrived",
  ];

  const destinationStageActions = ["completed", "dropoff", "dropped_off"];
  const stageDistanceThresholdKm = 0.5;

  const isNearPickup =
    eventCoordinates &&
    pickupCoordinates &&
    getDistanceInKm(eventCoordinates, pickupCoordinates) <= stageDistanceThresholdKm;

  const isNearDestination =
    eventCoordinates &&
    destinationCoordinates &&
    getDistanceInKm(eventCoordinates, destinationCoordinates) <=
      stageDistanceThresholdKm;

  if (pickupStageActions.some((key) => action.includes(key)) && isNearPickup) {
    return pickupLabel;
  }

  if (
    destinationStageActions.some((key) => action.includes(key)) &&
    isNearDestination
  ) {
    return destinationLabel;
  }

  return null;
};

const getLocationName = (event, ride) => {
  const stageLocation = getStageLocationLabel(event, ride);
  if (stageLocation) return stageLocation;

  const eventCoordinates = getEventCoordinates(event);
  if (!eventCoordinates) return "Location unavailable";

  const possibleLocations = [
    {
      label: "Pickup Location",
      address: ride?.pickupAddress,
      coordinates: ride?.pickupLocation,
    },
    {
      label: "Destination Location",
      address: ride?.destinationAddress,
      coordinates: ride?.dropLocation,
    },
    ...(ride?.stops || []).map((stop, index) => ({
      label: `Stop ${index + 1}`,
      address: stop?.address || "",
      coordinates:
        getCoordinatesFromValue(stop?.coordinates) ||
        getCoordinatesFromValue(stop?.location),
    })),
  ].filter((location) => location?.coordinates);

  let closestLocation = null;
  let shortestDistance = Number.POSITIVE_INFINITY;

  possibleLocations.forEach((location) => {
    const distance = getDistanceInKm(eventCoordinates, location.coordinates);
    if (distance < shortestDistance) {
      shortestDistance = distance;
      closestLocation = location;
    }
  });

  if (closestLocation && shortestDistance <= 0.5) {
    return closestLocation.address
      ? `${closestLocation.label}: ${closestLocation.address}`
      : closestLocation.label;
  }

  return "En route location";
};

const getShortLocationName = (locationText) => {
  if (!locationText) return "Location unavailable";
  if (locationText.includes(":")) {
    const parts = locationText.split(":");
    return parts.slice(1).join(":").trim() || locationText;
  }
  return locationText.split(",")[0]?.trim() || locationText;
};

const buildPreciseAddress = (address = {}) => {
  const streetLine = [
    address.house_number,
    address.road || address.pedestrian || address.footway,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  const localityLine = [
    address.neighbourhood,
    address.suburb,
    address.city_district,
    address.city || address.town || address.village,
  ]
    .filter(Boolean)
    .join(", ")
    .trim();

  const areaLine = [address.state_district, address.state, address.country]
    .filter(Boolean)
    .join(", ")
    .trim();

  const fullAddress = [streetLine, localityLine, areaLine]
    .filter(Boolean)
    .join(", ")
    .trim();

  // Prefer street-level detail when available; only then include broader area.
  if (streetLine) {
    return [streetLine, localityLine].filter(Boolean).join(", ").trim();
  }

  return fullAddress || null;
};

const normalizeTimestampMillis = (value) => {
  if (value == null) return null;

  if (typeof value === "number") {
    // Handle epoch seconds and epoch millis safely.
    return value < 1e12 ? value * 1000 : value;
  }

  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  // Firestore Timestamp-like shape: { seconds, nanoseconds }
  if (typeof value === "object" && typeof value.seconds === "number") {
    return value.seconds * 1000;
  }

  return null;
};

const toSafeISOString = (value) => {
  const millis = normalizeTimestampMillis(value);
  if (!millis) return null;
  const date = new Date(millis);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const getBestLocationLabel = (fallbackName, reverseName, eventCoordinates) => {
  if (fallbackName !== "En route location") {
    return getShortLocationName(fallbackName);
  }
  if (reverseName && reverseName !== "Unknown location") {
    // Do not truncate reverse geocode output; preserve precise street/building text.
    return reverseName;
  }
  if (
    eventCoordinates?.latitude != null &&
    eventCoordinates?.longitude != null &&
    !Number.isNaN(Number(eventCoordinates.latitude)) &&
    !Number.isNaN(Number(eventCoordinates.longitude))
  ) {
    return `Coordinates (${Number(eventCoordinates.latitude).toFixed(5)}, ${Number(
      eventCoordinates.longitude
    ).toFixed(5)})`;
  }
  return "En route location";
};

const getReverseGeocodeLocationName = async (latitude, longitude) => {
  try {
    const osmRes = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`
    );
    const osmData = await osmRes.json();
    const preciseAddress = buildPreciseAddress(osmData?.address);
    return (
      preciseAddress ||
      osmData?.display_name ||
      osmData?.name ||
      osmData?.address?.road ||
      "Unknown location"
    );
  } catch (error) {
    return "Unknown location";
  }
};

const isCancellationEvent = (event) => {
  const action = event?.action?.toLowerCase() || "";
  const journeyType = event?.journeyType?.toLowerCase() || "";
  return (
    action.includes("cancel") ||
    journeyType === "cancellation" ||
    !!event?.cancellationStatus
  );
};

const RideHistoryDetail = () => {
  const { id } = useParams();
  const [ride, setRide] = useState(null);
  const [payments, setPayments] = useState([]);
  const [passengerName, setPassengerName] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [reverseGeocodeNames, setReverseGeocodeNames] = useState({});
  const [mapRefreshKeys, setMapRefreshKeys] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRide = async () => {
      try {
        const docRef = doc(db, COLLECTION_NAME.ridesHistory, id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const rideData = { id: docSnap.id, ...docSnap.data() };
          setRide(rideData);

          // Fetch payments based on ride.id
          const paymentsRef = collection(db, COLLECTION_NAME.payments);
          const q = query(paymentsRef, where("rideId", "==", rideData.id));
          const snapshot = await getDocs(q);
          const paymentList = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setPayments(paymentList);

          const userId = rideData?.userId;

          if (userId) {
            const userRef = doc(db, COLLECTION_NAME.users, userId);
            const userSnap = await getDoc(userRef);

            if (userSnap.exists()) {
              const fullName = userSnap.data().fullName;
              setPassengerName(fullName);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching Rides or payment detail:", error);
        toast.error(
          "Error fetching Rides or payment detail. Please try again.",
          {
            position: "top-right",
            autoClose: 5000,
            transition: Bounce,
          }
        );
      } finally {
        setLoading(false);
      }
    };

    fetchRide();
  }, [id]);

  const sharedColumns = [
    { Header: "ID", accessor: "id" },
    { Header: "Amount", accessor: "amount" },
    { Header: "Currency", accessor: "currency" },
    { Header: "Type", accessor: "type" },
    {
      Header: "Payment Status",
      accessor: "paymentStatus",
      Cell: ({ value }) => {
        const status = value?.toLowerCase();

        let color = "gold";
        if (status === "transmitted") color = "green";
        else if (status === "voided") color = "red";

        return <Tag color={color}>{value}</Tag>;
      },
    },
    {
      Header: "Created At",
      accessor: "createdAt",
      Cell: ({ value }) => (value ? formatDate(new Date(value * 1000)) : "N/A"),
    },
  ];

  const refundColumns = [
    ...sharedColumns,
    {
      Header: "Refund Reason",
      accessor: "refundReason",
      Cell: ({ value }) => formatReason(value),
    },
  ];

  const paymentColumns = [...sharedColumns];

  const paymentData = payments
    .filter((p) => p.type?.toLowerCase() === "payment")
    .map((p) => ({
      id: p.id,
      amount: p.amount,
      currency: p.currency,
      paymentStatus: p.paymentStatus,
      refundReason: p.refundReason,
      type: p.type,
      createdAt: p.createdAt?.seconds || null,
    }));

  const refundData = payments
    .filter((p) => p.type?.toLowerCase() === "refund")
    .map((p) => ({
      id: p.id,
      amount: p.amount,
      currency: p.currency,
      paymentStatus: p.paymentStatus,
      refundReason: p.refundReason,
      type: p.type,
      createdAt: p.createdAt?.seconds || null,
    }));

  const buildJourneyActions = (rideData) => {
    const baseActions = Object.values(rideData?.actionLocationHistory || {});
    const actions = [...baseActions];
    const latestKnownCoordinates =
      [...actions]
        .sort((a, b) => (a?.timestampMillis || 0) - (b?.timestampMillis || 0))
        .map((event) => getEventCoordinates(event))
        .filter(Boolean)
        .pop() || null;

    const hasCreatedEvent = actions.some((event) => {
      const action = event?.action?.toLowerCase() || "";
      return action === "created" || action === "pending" || action === "scheduled";
    });

    if (!hasCreatedEvent && rideData?.createdAtMillis) {
      actions.push({
        action: "created",
        coordinates: rideData?.pickupLocation || null,
        journeyType: "ride_action",
        timestampMillis: rideData.createdAtMillis,
        timestampISO: rideData?.createdAtISO || null,
      });
    }

    const hasCancellationEvent = actions.some((event) => isCancellationEvent(event));
    if (rideData?.cancellationStatus && !hasCancellationEvent) {
      const cancellationTimestamp = normalizeTimestampMillis(
        rideData?.cancelledAt ||
        rideData?.updatedAtMillis ||
        rideData?.updatedAt ||
        rideData?.createdAtMillis
      );
      actions.push({
        action: rideData.cancellationStatus,
        cancellationStatus: rideData.cancellationStatus,
        cancelledBy: rideData?.cancelledBy || null,
        coordinates: latestKnownCoordinates,
        journeyType: "cancellation",
        timestampMillis: cancellationTimestamp,
        timestampISO: toSafeISOString(cancellationTimestamp),
      });
    }

    const hasCompletedEvent = actions.some(
      (event) => event?.action?.toLowerCase() === "completed"
    );
    if (
      !rideData?.cancellationStatus &&
      !hasCompletedEvent &&
      (rideData?.status?.toLowerCase() === "completed" ||
        rideData?.socketStatus?.toLowerCase() === "completed")
    ) {
      const completionTimestamp = normalizeTimestampMillis(
        rideData?.updatedAtMillis || rideData?.updatedAt
      );
      actions.push({
        action: "completed",
        coordinates:
          getCoordinatesFromValue(rideData?.dropLocation) || latestKnownCoordinates,
        journeyType: "ride_action",
        timestampMillis: completionTimestamp,
        timestampISO: toSafeISOString(completionTimestamp),
      });
    }

    return actions.sort((a, b) => {
      const timeA = a?.timestampMillis || 0;
      const timeB = b?.timestampMillis || 0;
      return timeA - timeB;
    });
  };

  const formatCoordinate = (value) => {
    if (value == null || Number.isNaN(Number(value))) return "N/A";
    return Number(value).toFixed(6);
  };

  const journeyActions = buildJourneyActions(ride);
  const isCancellationJourney =
    !!ride?.cancellationStatus || journeyActions.some((event) => isCancellationEvent(event));

  const actionColorClass = (action) => {
    const actionKey = action?.toLowerCase();
    if (actionKey?.includes("cancel")) return "bg-red-500";
    if (actionKey === "completed") return "bg-green-500";
    if (actionKey === "arrived") return "bg-blue-500";
    if (actionKey === "accepted") return "bg-dazzledBlue";
    return "bg-primary";
  };

  useEffect(() => {
    const resolveLocationNames = async () => {
      if (!ride || journeyActions.length === 0) return;

      const unresolvedActions = journeyActions
        .map((event, index) => ({
          event,
          key: event?.timestampMillis || `${ride?.id}-${index}`,
        }))
        .filter(({ event, key }) => {
          const fallbackName = getLocationName(event, ride);
          return (
            fallbackName === "En route location" &&
            !!getEventCoordinates(event) &&
            !reverseGeocodeNames[key]
          );
        });

      if (unresolvedActions.length === 0) return;

      const responses = await Promise.all(
        unresolvedActions.map(async ({ event, key }) => {
          const eventCoordinates = getEventCoordinates(event);
          if (!eventCoordinates) {
            return { key, name: "Unknown location" };
          }
          const lat = eventCoordinates.latitude;
          const lon = eventCoordinates.longitude;
          try {
            const name = await getReverseGeocodeLocationName(lat, lon);
            return { key, name };
          } catch (error) {
            return { key, name: "Unknown location" };
          }
        })
      );

      setReverseGeocodeNames((prev) => {
        const next = { ...prev };
        responses.forEach(({ key, name }) => {
          next[key] = name;
        });
        return next;
      });
    };

    resolveLocationNames();
  }, [ride, journeyActions, reverseGeocodeNames]);

  return (
    <div className="px-1 sm:px-1 md-px-4 min-h-screen">
      <h1 className="text-2xl md:text-3xl font-bold mt-8 md:mt-5 mb-2 md:mb-5 text-primary">
        <button
          onClick={() => navigate(-1)}
          className="text-primary text-2xl hover:text-dazzledBlue transition mr-3"
        >
          <FiArrowLeft />
        </button>
        Ride Details
      </h1>

      {loading ? (
        <Loader />
      ) : (
        ride && (
          <div>
            <Collapse
              defaultActiveKey={["rideDetails", "rideJourney", "payment", "refund"]}
              style={{ marginTop: "2rem" }}
            >
              <Collapse.Panel
                header={
                  <h3 className="text-lg font-semibold text-dazzledBlue">
                    Ride Details
                  </h3>
                }
                key="rideDetails"
              >
                <Descriptions
                  bordered
                  column={{ xs: 1, sm: 2, md: 2 }}
                  layout="horizontal"
                  size="middle"
                >
                  <Descriptions.Item label="Ride ID">{ride.id}</Descriptions.Item>
                  <Descriptions.Item label="Original Price">
                    $
                    {ride?.cancellationStatus
                      ? ride?.originalPrice ?? ride?.price
                      : ride?.price}
                  </Descriptions.Item>
                  <Descriptions.Item label="Tip">
                    ${ride.tip ? parseFloat(ride.tip).toFixed(2) : "0.00"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Created At">
                    {ride?.createdAt ? formatFirebaseTime(ride.createdAt) : "N/A"}
                  </Descriptions.Item>
                  {ride?.isScheduled && ride?.scheduledTime && (
                    <Descriptions.Item label="Scheduled Time">
                      {ride.scheduledTimeMillis
                        ? new Date(ride.scheduledTimeMillis).toLocaleDateString(
                          "en-US",
                          {
                            weekday: "short",
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                            hour: "numeric",
                            minute: "2-digit",
                            hour12: true,
                          }
                        )
                        : formatFirebaseTime(ride.scheduledTime)}
                    </Descriptions.Item>
                  )}
                  {/* <Descriptions.Item label="Total Amount">
                    ${parseFloat(ride.price || 0).toFixed(2)}
                  </Descriptions.Item> */}
                  <Descriptions.Item label="Status">
                    {"cancellationStatus" in ride ? (
                      <span>Cancelled</span>
                    ) : (
                      ride.status || "-----"
                    )}
                  </Descriptions.Item>
                  {ride.cancellationStatus && (
                    <Descriptions.Item label="Cancellation Status">
                      {formatReason(ride.cancellationStatus)}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label="Payment Status">
                    {ride.paymentStatus}
                  </Descriptions.Item>
                  <Descriptions.Item label="Passenger Name">
                    {passengerName || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Driver Name">
                    {ride.riderName || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Number of Passengers">
                    {ride.numberOfPassenger}
                  </Descriptions.Item>
                  <Descriptions.Item label="Pickup Address">
                    {ride.pickupAddress}
                  </Descriptions.Item>
                  <Descriptions.Item label="Pickup Zone">
                    {ride.pickupZone}
                  </Descriptions.Item>
                  {ride.stops && ride.stops.length > 0 && (
                    <Descriptions.Item label="Stops" span={2}>
                      <div className="space-y-2">
                        {ride.stops.map((stop, index) => (
                          <div
                            key={index}
                            className="border-b pb-2 last:border-b-0"
                          >
                            <div className="flex items-start gap-2">
                              <span className="font-semibold text-dazzledBlue">
                                Stop {index + 1}:
                              </span>
                              <span>{stop.address || stop}</span>
                            </div>
                            {(stop.waitTime || ride.stopsWaitTime?.[index]) && (
                              <div className="flex items-center gap-2 ml-16 mt-1">
                                <span className="text-gray-600 text-sm">
                                  Wait Time:
                                </span>
                                <span className="text-gray-800 font-medium text-sm">
                                  {stop.waitTime || ride.stopsWaitTime?.[index]}{" "}
                                  minutes
                                </span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label="Destination Address">
                    {ride.destinationAddress}
                  </Descriptions.Item>
                  <Descriptions.Item label="Destination Zone">
                    {ride.destinationZone}
                  </Descriptions.Item>
                  <Descriptions.Item label="Ride Type">
                    {ride.isScheduled ? "Scheduled Ride" : "Normal Ride"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Retained Amount">
                    ${ride?.price}
                  </Descriptions.Item>
                  <Descriptions.Item label="Driver Share">
                    ${ride?.driverPrice ? ride.driverPrice : "0"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Swyft Share">
                    $
                    {ride?.swyftPrice
                      ? ride.swyftPrice
                      : ride?.price - ride?.driverPrice}
                  </Descriptions.Item>
                  {!ride.cancellationStatus && (
                    <Descriptions.Item label="Completed At">
                      {ride?.updatedAt
                        ? new Date(ride.updatedAt).toLocaleDateString("en-US", {
                          weekday: "short",
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                          hour12: true,
                        })
                        : "N/A"}
                    </Descriptions.Item>
                  )}
                  {ride.cancellationStatus && (
                    <Descriptions.Item label="Cancellation At">
                      {ride?.cancelledAt
                        ? formatFirebaseTime(ride.cancelledAt)
                        : "N/A"}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Collapse.Panel>

              {journeyActions.length > 0 && (
                <Collapse.Panel
                  header={
                    <h3 className="text-lg font-semibold text-dazzledBlue">
                      Ride Journey
                    </h3>
                  }
                  key="rideJourney"
                >
                  <p className="text-sm text-gray-500 mb-4">
                    {isCancellationJourney
                      ? "Complete cancellation timeline across the ride journey."
                      : "Complete action timeline across the ride journey."}
                  </p>

                  <div className="space-y-4">
                    {journeyActions.map((event, index) => {
                      const eventCoordinates = getEventCoordinates(event);
                      return (
                        <div key={event?.timestampMillis || index} className="flex">
                          <div className="flex flex-col items-center mr-4">
                            <span
                              className={`w-3 h-3 rounded-full mt-1 ${actionColorClass(
                                event?.action
                              )}`}
                            />
                            {index !== journeyActions.length - 1 && (
                              <span className="w-px flex-1 bg-gray-300 mt-1" />
                            )}
                          </div>

                          <div className="flex-1 pb-2">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                              <h4 className="font-semibold text-primary">
                                {formatActionLabel(event?.action)}
                              </h4>
                              <span className="text-xs md:text-sm text-gray-500">
                                {event?.timestampMillis
                                  ? new Date(event.timestampMillis).toLocaleString(
                                    "en-US",
                                    {
                                      weekday: "short",
                                      month: "short",
                                      day: "numeric",
                                      year: "numeric",
                                      hour: "numeric",
                                      minute: "2-digit",
                                      hour12: true,
                                    }
                                  )
                                  : event?.timestampISO || "N/A"}
                              </span>
                            </div>

                            <div className="text-sm text-gray-700 mt-1">
                              <span className="font-medium text-gray-600">
                                Location:
                              </span>{" "}
                              {(() => {
                                const eventKey =
                                  event?.timestampMillis || `${ride?.id}-${index}`;
                                const fallbackName = getLocationName(event, ride);
                                return getBestLocationLabel(
                                  fallbackName,
                                  reverseGeocodeNames[eventKey],
                                  eventCoordinates
                                );
                              })()}
                            </div>
                            {(() => {
                              const eventKey =
                                event?.timestampMillis || `${ride?.id}-${index}`;
                              const fallbackName = getLocationName(event, ride);
                              const resolvedLocation = getBestLocationLabel(
                                fallbackName,
                                reverseGeocodeNames[eventKey],
                                eventCoordinates
                              );

                              return (
                                <>
                                  <div className="text-sm text-gray-700 mt-1">
                                    <span className="font-medium text-gray-600">
                                      Coordinates:
                                    </span>{" "}
                                    {formatCoordinate(eventCoordinates?.latitude)},{" "}
                                    {formatCoordinate(eventCoordinates?.longitude)}
                                  </div>

                                  {eventCoordinates && (
                                    <div className="mt-3 border border-gray-200 rounded-md overflow-hidden group relative">
                                      <div className="flex justify-end px-3 py-2 border-b border-gray-200 bg-gray-50">
                                        <button
                                          onClick={() => {
                                            const eventKey =
                                              event?.timestampMillis ||
                                              `${ride?.id}-${index}`;
                                            setMapRefreshKeys((prev) => ({
                                              ...prev,
                                              [eventKey]: (prev[eventKey] || 0) + 1,
                                            }));
                                          }}
                                          className="text-xs font-medium text-dazzledBlue hover:text-primary transition"
                                        >
                                          Recenter
                                        </button>
                                      </div>
                                      <div className="absolute left-3 top-12 z-10 opacity-0 group-hover:opacity-100 transition pointer-events-none">
                                        <div className="bg-black bg-opacity-80 text-white text-xs px-2 py-1 rounded shadow-md max-w-[85%]">
                                          {resolvedLocation} (
                                          {formatCoordinate(eventCoordinates.latitude)},{" "}
                                          {formatCoordinate(eventCoordinates.longitude)})
                                        </div>
                                      </div>
                                      <iframe
                                        key={`${event?.timestampMillis || `${ride?.id}-${index}`
                                          }-${mapRefreshKeys[
                                          event?.timestampMillis || `${ride?.id}-${index}`
                                          ] || 0}`}
                                        title={`Action map ${event?.timestampMillis || index}`}
                                        src={getMapEmbedUrl(
                                          eventCoordinates.latitude,
                                          eventCoordinates.longitude
                                        )}
                                        className="w-full h-44"
                                        loading="lazy"
                                      />
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {ride?.completionEvidence?.imageUrl && (
                    <div className="mt-6 pt-4 border-t border-gray-200">
                      <h4 className="font-semibold text-primary mb-2">
                        Completion Evidence
                      </h4>
                      <img
                        src={ride.completionEvidence.imageUrl}
                        alt="Ride completion evidence"
                        onClick={() =>
                          setSelectedImage(ride.completionEvidence.imageUrl)
                        }
                        className="w-36 h-36 object-cover rounded-md border border-gray-200 hover:opacity-90 transition cursor-pointer"
                      />
                    </div>
                  )}
                </Collapse.Panel>
              )}

              <Collapse.Panel
                header={
                  <h3 className="text-lg font-semibold text-dazzledBlue">
                    Payment Details
                  </h3>
                }
                key="payment"
              >
                {paymentData.length > 0 ? (
                  <Table
                    columns={paymentColumns}
                    data={paymentData}
                    screen="ridesHistory"
                  />
                ) : (
                  <p>No payment data available.</p>
                )}
              </Collapse.Panel>

              <Collapse.Panel
                header={
                  <h3 className="text-lg font-semibold text-dazzledBlue">
                    Refund Details
                  </h3>
                }
                key="refund"
              >
                {refundData.length > 0 ? (
                  <Table
                    columns={refundColumns}
                    data={refundData}
                    screen="ridesHistory"
                  />
                ) : (
                  <p>No refund data available.</p>
                )}
              </Collapse.Panel>
            </Collapse>

            {selectedImage && (
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-80 z-50">
                <div className="relative">
                  <img
                    src={selectedImage}
                    alt="Full View"
                    className="max-h-[70vh] rounded-lg shadow-lg"
                  />
                  <button
                    onClick={() => setSelectedImage(null)}
                    className="absolute top-1 right-3 text-3xl text-red-700"
                  >
                    &times;
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
};

export default RideHistoryDetail;

